import { Box, Button, Group, Image, Modal, SimpleGrid, Text, UnstyledButton, useMantineTheme } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { client } from "../../lib/axiosClient";
import Cookies from "universal-cookie";
import { useShipping } from "../../api";

const cookies = new Cookies(null, { path: '/' });

type Props = {
    opened: boolean;
    onClose: () => void;
    selectedOrders: any[];
    refetchData: () => void;
};

export const BulkDeliveryModal = ({ opened, onClose, selectedOrders, refetchData }: Props) => {
    const { t } = useTranslation();
    const theme = useMantineTheme();
    const [loading, setLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<string | null>(null);

    const { data: dataShipping } = useShipping();
    const [allDeliveryCompany, setAllDeliveryCompany] = useState<any[]>([]);

    useEffect(() => {
        if (dataShipping.length >= 0) {
            let newData: { label: string, value: string }[] = []
            dataShipping.map((item: any) => {
                newData.push({ label: item.name, value: item._id.toString() })
            })
            setAllDeliveryCompany(newData)
        }
    }, [dataShipping]);

    const handleConfirm = async () => {
        if (!selectedCompany) return;
        setLoading(true);

        const companyId = dataShipping.find((item: any) => item.name === selectedCompany)?._id;

        if (!companyId) {
            notifications.show({ message: "Error finding company ID", color: 'red' });
            setLoading(false);
            return;
        }

        try {
            const promises = selectedOrders.map(order => {
                return client.put(`/orders/${order._id}`, {
                    addToDeliveryCompany: true,
                    // We only update the status and delivery company.
                    // We assume other fields (name, phone, etc.) are already correct.
                    // IMPORTANT: The backend 'updateDelivery' might require other fields if it does a full replace or validation?
                    // Looking at the controller: "if(Object.keys(content).length > 0) ... findByIdAndUpdate".
                    // It seems to just update the passed fields.
                    // BUT for "addToDeliveryCompany" logic, it reads fields from the EXISTING document (deliveryProduct[0]).
                    // So we only need to pass the triggers.
                    status: "confirmed", // Logic in backend: if (addToDeliveryCompany === true && "status" in content && content?.status === "confirmed")
                    deliveryCompany: companyId
                }, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': cookies.get('token') || ""
                    }
                });
            });

            await Promise.all(promises);

            notifications.show({ message: "تم ارسال الطلبات بنجاح", color: 'green' });
            refetchData();
            handleClose();
        } catch (error) {
            console.error(error);
            notifications.show({ message: "حدث خطأ أثناء الارسال", color: 'red' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedCompany(null);
        onClose();
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={`ارسال ${selectedOrders.length} طلبات لشركة التوصيل`}
            size="lg"
            centered
        >
            <Box p={20}>
                <Text fz={14} fw={500} mb={10}>{t('modals.confirmedOrder.label00')}</Text>

                {allDeliveryCompany.length > 0 ? (
                    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing={10}>
                        {allDeliveryCompany.map((item: any) => {
                            const selected = selectedCompany === item.label;
                            const shippingItem = dataShipping.find((s: any) => s._id === item.value);
                            return (
                                <UnstyledButton
                                    key={item.value}
                                    onClick={() => setSelectedCompany(item.label)}
                                    style={{
                                        border: selected ? `2px solid ${theme.colors.blue[6]}` : "1px solid #E0E2E7",
                                        borderRadius: 8,
                                        padding: 10,
                                        backgroundColor: selected ? "#F0F7FF" : "#fff",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.2s"
                                    }}
                                    mah={180}
                                >
                                    <Image
                                        src={`${process.env.REACT_APP_API_URL_IMAGES}/${shippingItem?.logo}`}
                                        h={40} w="100%" fit="contain" mb={5}
                                        fallbackSrc="https://placehold.co/60x40?text=Logo"
                                    />
                                    <Text size="sm" fw={500} ta="center">{item.label}</Text>
                                </UnstyledButton>
                            )
                        })}
                    </SimpleGrid>
                ) : (
                    <Text size="sm" c="dimmed">لا توجد شركات توصيل متاحة</Text>
                )}

                <Group justify="flex-end" mt={30}>
                    <Button variant="default" onClick={handleClose} disabled={loading}>الغاء</Button>
                    <Button
                        color="#323232"
                        onClick={handleConfirm}
                        loading={loading}
                        disabled={!selectedCompany}
                        leftSection={<IconCheck size={16} />}
                    >
                        تأكيد الارسال
                    </Button>
                </Group>
            </Box>
        </Modal>
    );
};
